import { useAuth0, type User } from "@auth0/auth0-react";
import { AUTH_ENABLED } from "./config";
import { markSignedOut } from "./signOut";

/** Prefers the OIDC `given_name` claim; falls back to the first word of `name`
 * (unless Auth0 filled `name` with the email, as it does for database users),
 * then the email's local part. */
function firstName(user: User): string | undefined {
  if (user.given_name) return user.given_name;
  if (user.name && !user.name.includes("@")) return user.name.split(/\s+/)[0];
  return user.email?.split("@")[0];
}

/** The signed-in user's first name (full email on hover) and a Sign out link. Signing out
 * marks the tab signed-out first, so the return from Auth0 lands on the "Signed out"
 * screen rather than silently logging back in. */
function AuthenticatedUserBadge() {
  const { user, logout } = useAuth0();
  const displayName = user && firstName(user);
  return (
    <>
      {displayName && (
        <span className="max-w-[12ch] truncate" title={user?.email ?? displayName}>
          {displayName}
        </span>
      )}
      <button
        onClick={() => {
          markSignedOut();
          void logout({ logoutParams: { returnTo: window.location.origin } });
        }}
        className="shrink-0 cursor-pointer whitespace-nowrap underline decoration-dotted underline-offset-2"
      >
        Sign out
      </button>
    </>
  );
}

/** Branches on which component to mount rather than conditionally calling a hook —
 * `useAuth0` requires an Auth0Provider ancestor, which only exists when AUTH_ENABLED
 * (see ./config.ts and ./AuthProvider.tsx). */
export function UserBadge() {
  if (!AUTH_ENABLED) return null;
  return <AuthenticatedUserBadge />;
}
